import { request } from "@src/utils/apiClient";
import AppUtils from "@src/utils/appUtils";
import SignolUtils from "@src/utils/signolUtils";
/** managers_service */
const BASE_MANAGER_SERVICE = "/managerDashboard";
/** spitfire_flightdata */
const BASE_MANAGER_LEGACY = "/manager";

export const getManagerProfile = async () => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/profile",
  });
};

/** Note: LEGACY backend */
export const fetchQuickSightDashboardUrl = async () => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/quicksight/embed-url",
  });
};

export const fetchHighestOperatorMilestoneTotals = async () => {
  return request({
    method: "GET",
    endpoint:
      BASE_MANAGER_SERVICE +
      "/stats/highest-operator-milestone-reached-savings",
  });
};

export const fetchEventTotals = async (teamId, startDate, endDate) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/stats/event-totals",
    params: {
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchLatestDataReceived = async (teamId) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/stats/latest-data-received",
    params: {
      teamIds: [teamId],
    },
  });
};

export const fetchBehaviourSuccessRateVsPreSignol = async (
  teamId,
  startDate,
  endDate,
) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/behaviours/success-rate-vs-pre",
    params: {
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchBehaviourSuccessRateOverTime = async (
  behaviourKey,
  teamId,
  startDate,
  endDate,
) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/behaviours/success-rate-over-time",
    params: {
      behaviourName: behaviourKey,
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchBehaviourFuelSavingVsPreSignol = async (
  teamId,
  startDate,
  endDate,
) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/behaviours/fuel-savings-vs-pre",
    params: {
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchBehaviourDataOverviewStatusCounts = async (
  behaviourKey,
  teamId,
  startDate,
  endDate,
) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/behaviours/status-counts",
    params: {
      behaviourName: behaviourKey,
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchBehaviourSuccessRatePerOperator = async (
  behaviourKey,
  teamId,
  startDate,
  endDate,
) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/behaviours/success-rate-per-operator",
    params: {
      behaviourName: behaviourKey,
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchBehaviourFuelSavingsPerOperator = async (
  behaviourKey,
  teamId,
  startDate,
  endDate,
) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/behaviours/fuel-savings-per-operator",
    params: {
      behaviourName: behaviourKey,
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchBehaviourFuelSavingsOverTime = async (
  behaviourKey,
  teamId,
  startDate,
  endDate,
) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/behaviours/fuel-savings-over-time",
    params: {
      behaviourName: behaviourKey,
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchPageViews = async (teamId, startDate, endDate) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/activities/page-views",
    params: {
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchNotificationTotals = async (teamId, startDate, endDate) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/notifications/total-per-type",
    params: {
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchNotificationsPerTemplatePerDay = async (
  teamId,
  startDate,
  endDate,
) => {
  return request({
    method: "GET",
    endpoint:
      BASE_MANAGER_SERVICE + "/notifications/calendar-per-template-per-day",
    params: {
      teamIds: AppUtils.isValidUUID(teamId) ? [teamId] : [],
      startDate,
      endDate,
    },
  });
};

export const fetchVehicles = async (teamId, startDate, endDate) => {
  return request({
    method: "GET",
    endpoint: BASE_MANAGER_SERVICE + "/vehicles",
    params: {
      teamIds: [teamId],
      startDate,
      endDate,
    },
  });
};

export const fetchTeamVehicles = async (
  teamId,
  startDate,
  endDate,
  { behaviourNames, searchQuery, success, vehicles, origin, destination } = {},
) => {
  const body = {
    behaviourNames,
    searchQuery,
    success,
    vehicles,
    origin,
    destination,
  };
  const hasAdvancedFilters = Object.values(body).some((d) => d != null);
  return request({
    method: hasAdvancedFilters ? "POST" : "GET",
    endpoint: BASE_MANAGER_SERVICE + "/teams/vehicles",
    params: {
      teamIds: [teamId],
      startDate,
      endDate,
    },
    body: hasAdvancedFilters ? body : undefined,
  });
};

export const fetchRoutes = async (
  teamId,
  startDate,
  endDate,
  { behaviourNames, searchQuery, success, vehicles, origin, destination } = {},
) => {
  const body = {
    behaviourNames,
    searchQuery,
    success,
    vehicles,
    origin,
    destination,
  };
  const hasAdvancedFilters = Object.values(body).some((d) => d != null);
  return request({
    method: hasAdvancedFilters ? "POST" : "GET",
    endpoint: BASE_MANAGER_SERVICE + "/routes",
    params: {
      teamIds: [teamId],
      startDate,
      endDate,
    },
    body: hasAdvancedFilters ? body : undefined,
  });
};

export const fetchFeedbackSearchResults = async (
  teamId,
  startDate,
  endDate,
  { behaviourNames, searchQuery, success, vehicles, origin, destination } = {},
) => {
  return request({
    method: "POST",
    endpoint: BASE_MANAGER_SERVICE + "/feedback/search",
    params: {
      teamIds: [teamId],
      startDate,
      endDate,
    },
    body: {
      behaviourNames,
      searchQuery,
      success,
      vehicles,
      origin,
      destination,
    },
  });
};

export const executeDataQuery = async (mode, sql) => {
  // Normalize SQL:
  // 1. Replace newlines and multiple spaces with single space
  // 2. Replace double quotes with single quotes (SQL standard)
  const normalizedSql = sql.replace(/\s+/g, " ").replace(/"/g, "'").trim();

  const queryParams = {};

  if (mode === "basic") {
    const { fields = [], from, to } = AppUtils.extractParamsFromQuery(sql);
    queryParams.fields = fields.join(","); // list of requested fields
    queryParams.from = from;
    queryParams.to = to;
  } else {
    // advanced mode (for admins only)
    queryParams.sql = normalizedSql;
  }

  return request({
    method: "GET",
    endpoint: `/deltaSharing/query_${mode}`,
    params: queryParams,
  });
};

export const downloadDataQuery = async ({ sql, headersFormat = "alias" }) => {
  const queryHash = AppUtils.base64encode(sql);
  return request({
    method: "GET",
    endpoint: "/deltaSharing/query_download",
    params: {
      queryHash,
      headersFormat,
    },
  });
};

export const fetchPlotlyGraphs = async (keys) => {
  const normalizedKeys = keys.map(SignolUtils.normalizeBehiveKey);
  return request({
    method: "POST",
    endpoint: BASE_MANAGER_SERVICE + "/graph-data/behive-outputs/download-many",
    body: {
      keys: normalizedKeys,
    },
  });
};
